import React from 'react';
import { Alert, Typography, Space } from 'antd';
import { WarningOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * PreVerificationCheck
 *
 * Analyzes bill data before verification attempt and warns about potential issues.
 * Helps users identify problems that will block verification.
 *
 * Props:
 *  - bill: KpBillReceipt object with items array
 */
const PreVerificationCheck = ({ bill }) => {
  if (!bill || !bill.items || bill.items.length === 0) return null;

  const issues = [];
  const warnings = [];

  // Calculate totals from items
  const itemsTotal = bill.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const itemsTaxable = bill.items.reduce((sum, item) => sum + (item.taxable_amount || 0), 0);
  const itemsTax = bill.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const itemsDiscount = bill.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);

  // Check for total mismatches (tolerance: ±1.00)
  const billAmountDiff = Math.abs((bill.bill_amount || 0) - itemsTotal);
  const taxableDiff = Math.abs((bill.taxable_amount || 0) - itemsTaxable);
  const taxDiff = Math.abs((bill.tax_amount || 0) - itemsTax);
  const discountDiff = Math.abs((bill.discount_amount || 0) - itemsDiscount);

  if (billAmountDiff > 1.0) {
    issues.push({
      type: 'critical',
      message: `Bill amount mismatch: Header shows ₹${bill.bill_amount?.toFixed(2)}, but items total ₹${itemsTotal.toFixed(2)} (diff: ₹${billAmountDiff.toFixed(2)})`
    });
  }

  if (taxableDiff > 1.0) {
    issues.push({
      type: 'critical',
      message: `Taxable amount mismatch: Header shows ₹${bill.taxable_amount?.toFixed(2)}, but items total ₹${itemsTaxable.toFixed(2)} (diff: ₹${taxableDiff.toFixed(2)})`
    });
  }

  if (taxDiff > 1.0) {
    issues.push({
      type: 'critical',
      message: `Tax amount mismatch: Header shows ₹${bill.tax_amount?.toFixed(2)}, but items total ₹${itemsTax.toFixed(2)} (diff: ₹${taxDiff.toFixed(2)})`
    });
  }

  if (discountDiff > 1.0) {
    issues.push({
      type: 'critical',
      message: `Discount amount mismatch: Header shows ₹${bill.discount_amount?.toFixed(2)}, but items total ₹${itemsDiscount.toFixed(2)} (diff: ₹${discountDiff.toFixed(2)})`
    });
  }

  // Check for item-level critical issues
  const criticalItems = bill.items.filter(item => {
    const note = item.reconciliation_note || '';
    return (
      note.includes('CRITICAL') ||
      note.includes('OVERBILLED') ||
      note.includes('overcharged') ||
      note.includes('TAX MISMATCH') ||
      note.includes('LESS discount')
    );
  });

  if (criticalItems.length > 0) {
    issues.push({
      type: 'critical',
      message: `${criticalItems.length} item(s) have critical mismatches that will block verification`
    });
  }

  // Check for warnings (non-critical but worth noting)
  const underbilledItems = bill.items.filter(item => {
    const note = item.reconciliation_note || '';
    return note.includes('underbilled') || note.includes('partial delivery');
  });

  if (underbilledItems.length > 0) {
    warnings.push({
      type: 'warning',
      message: `${underbilledItems.length} item(s) have partial delivery (underbilled) - This is acceptable`
    });
  }

  const underchargedItems = bill.items.filter(item => {
    const note = item.reconciliation_note || '';
    return note.includes('undercharged') && !note.includes('CRITICAL');
  });

  if (underchargedItems.length > 0) {
    warnings.push({
      type: 'warning',
      message: `${underchargedItems.length} item(s) have better pricing than PO - This is acceptable`
    });
  }

  // If no issues, don't display anything
  if (issues.length === 0 && warnings.length === 0) {
    return (
      <Alert
        message={
          <Space>
            <InfoCircleOutlined style={{ color: '#52c41a' }} />
            <Text>No issues detected. Bill is ready for verification.</Text>
          </Space>
        }
        type="success"
        style={{ marginBottom: 16 }}
        showIcon={false}
      />
    );
  }

  const hasCritical = issues.length > 0;

  return (
    <Alert
      message={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasCritical ? <CloseCircleOutlined /> : <WarningOutlined />}
          <Text strong>
            {hasCritical ? 'Verification Will Be Blocked' : 'Pre-Verification Check'}
          </Text>
        </div>
      }
      description={
        <div style={{ marginTop: 12 }}>
          {issues.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8, color: '#cf1322' }}>
                Critical Issues (Will Block Verification):
              </Text>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                {issues.map((issue, idx) => (
                  <li key={idx} style={{ marginBottom: 6, color: '#000' }}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warnings.length > 0 && (
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8, color: '#faad14' }}>
                Notes (Acceptable):
              </Text>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                {warnings.map((warning, idx) => (
                  <li key={idx} style={{ marginBottom: 6, color: '#000' }}>
                    {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasCritical && (
            <div style={{
              marginTop: 12,
              padding: 10,
              background: '#fff1f0',
              border: '1px solid #ffa39e',
              borderRadius: 4
            }}>
              <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Action Required:
              </Text>
              <Text style={{ fontSize: 12 }}>
                Correct the bill data or reject this bill before verification can proceed.
                The system enforces strict validation to prevent financial errors.
              </Text>
            </div>
          )}
        </div>
      }
      type={hasCritical ? 'error' : 'warning'}
      showIcon={false}
      style={{ marginBottom: 16 }}
    />
  );
};

export default PreVerificationCheck;
